import { flow, observable, computed, makeObservable } from 'mobx'
import { promisedComputed } from '../../utils/computed-async-mobx'
import toJS from '../../utils/tojs'
import toClipboard from '../../utils/toClipboard'
import { getRoot, types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Mapping from './mapping'
import { fnFromCode } from '../../utils/fn'
import { proxy, releaseProxy } from 'comlink/dist/esm/comlink.mjs'
import { getWorker } from '../../utils/worker'
import { getCookie, setCookie } from '../../utils/cookie'

const JSConfig = t
  .model('JS', {
    type: t.optional(t.string, 'JavaScript'),
    js: t.maybeNull(t.string),
    inputs: t.maybeNull(t.late(() => Mapping)),
    context: t.maybeNull(t.late(() => Mapping))
  })
  .views(self => ({
    get icon() {
      return 'function'
    },
    get provider () {
      return JSProvider
    }
  }))
  .views(self => ({
    get cleanInputs () {
      if (!self.inputs || !self.inputs.children || !self.inputs.children.length) { return [] }
      return (self.inputs && self.inputs.children) || []
    },
    get code () {
      return fnFromCode(self.js, self.cleanInputs)
    },
    get mapping () {
      const m = Mapping.create({ type: 'object' }, { ro: true, parentDataSource: self })
      m.children = [self.inputs, self.context].filter(Boolean)
      return m
    },
    labels () {
      return {
        key: self.id,
        label: self.name
      }
    }
  }))
  .actions(crudActions)
  .actions(self => ({
    afterCreate () {
      self.js = self.js || ''
      self.inputs = self.inputs || { type: 'object' }
    }
  }))

function convertArgs(args) {
  return args.map(convertArg)
}

function convertArg(a) {
  if (typeof a === 'function') {
    const func = proxy(async (...fa) => toJS(await a(...fa)))
    return func
  }
  if (a && typeof a === 'object') return toJS(a)
  return a
}

function convertContext(context) {
  return Object.keys(context).reduce((o, k) => ({...o, [k]: convertArg(context[k])}), {})
}

const tellParent = d => typeof parent !== 'undefined' && parent.postMessage(d, '*')
const scrollTo = (...args) => typeof window !== 'undefined' && window.scrollTo(...args)
export class JSProvider {
  constructor (config) {
    makeObservable(this, {
      data: computed,
      params: computed
    })
    this._data = observable.box(undefined, { deep: false })
    this.config = config
  }

  runCode = async (code, inputs = [], params = [], mapping) => {
    await this.config?.mapping.mapper.whenReady()
    let context = {}
    const c = this.config?.context
    if(c?.children) {
      const props = c.toProps || {}
      for(let {key} of c.children) {
        context[key] = props[key]
      }
    }

    const ctx = convertContext({
      ...context,
      toClipboard,
      getCookie,
      setCookie,
      tellParent,
      scrollTo
    })
    const worker = await getWorker(ctx)

    let result
    const args = toJS(params.slice(0, inputs?.length))
    try {
      let r
      // swc uses regenerator transforms and can't be turned off atm.
      // this gets transformed to `worker.exec.apply` and the
      // (comlink) proxy gets confused looking for an `apply` key
      eval(`r = worker.exec(code, ...convertArgs(args))`)
      result = await r
    } catch (e) {
      const prettyPath =  mapping && typeof window !== 'undefined' && window.store.prettyPrintPath(mapping)
      console.group(prettyPath ?? 'javascript')
      console.log(e)
      console.groupEnd()
      throw e
    } finally {
      worker[releaseProxy]()
    }
    
    return result
  }

  run = (...params) => {
    return this.runCode(
      this.config.code,
      this.config.inputs && this.config.inputs.children,
      params
    )
  }

  get data () {
    return this._data.get()
  }

  get params () {
    return Object.values((this.config.inputs && this.config.inputs.toProps) || {})
  }

  query = flow(function * () {
    try {
      const results = yield this.run(...this.params)
      this._data.set(results)
      return results
    } catch (e) {
      console.error(e)
    }
  })
}

export default JSConfig
